import {Box, Typography, Tooltip} from '@mui/material'
import {ChevronRight} from '@mui/icons-material'
import {useState} from 'react'
import {Version} from './types'

interface VersionChangeSummaryProps {
    version: Version
    previousVersion: Version | null
    compareVersion?: Version | null
    versionFileContent: any
    previousVersionFileContent: any
    rateTableContent?: any
    previousRateTableContent?: any
    versionKeysContent?: string[] | null
    compareVersionKeysContent?: string[] | null
    formulasLoadError?: string | null
    versionKeysLoadError?: string | null
    rateTablesLoadError?: string | null
    onNavigateToFormula?: (formulaPath: string) => void
    onNavigateToRateTable?: (tableName: string) => void
    hoveredVersionKey?: string | null
    onVersionKeyHover?: (versionKey: string | null) => void
    pinnedVersionKey?: string | null
    onVersionKeyTogglePin?: (versionKey: string) => void
    collapsedDomains?: Set<string>
    setCollapsedDomains?: (value: Set<string>) => void
    collapsedCoverages?: Set<string>
    setCollapsedCoverages?: (value: Set<string>) => void
    versionKeysFilter?: Set<string>
    setVersionKeysFilter?: (value: Set<string>) => void
    formulasFilter?: Set<string>
    setFormulasFilter?: (value: Set<string>) => void
    rateTablesFilter?: Set<string>
    setRateTablesFilter?: (value: Set<string>) => void
}

function stringifySorted(obj: unknown): string {
    return JSON.stringify(sortRecursively(obj));
}

function sortRecursively<T>(value: T): T {
    if (Array.isArray(value)) {
        return value.map(sortRecursively) as T;
    }

    if (value !== null && typeof value === "object") {
        const sorted: Record<string, unknown> = {};

        for (const key of Object.keys(value).sort()) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            sorted[key] = sortRecursively((value as any)[key]);
        }

        return sorted as T;
    }

    return value;
}

function VersionChangeSummary({
                                  version,
                                  previousVersion,
                                  compareVersion,
                                  versionFileContent,
                                  previousVersionFileContent,
                                  rateTableContent,
                                  previousRateTableContent,
                                  versionKeysContent,
                                  compareVersionKeysContent,
                                  formulasLoadError,
                                  versionKeysLoadError,
                                  rateTablesLoadError,
                                  onNavigateToFormula,
                                  onNavigateToRateTable,
                                  hoveredVersionKey,
                                  onVersionKeyHover,
                                  pinnedVersionKey,
                                  onVersionKeyTogglePin,
                                  collapsedDomains: collapsedDomainsProp,
                                  setCollapsedDomains: setCollapsedDomainsProp,
                                  collapsedCoverages: collapsedCoveragesProp,
                                  setCollapsedCoverages: setCollapsedCoveragesProp,
                                  versionKeysFilter: versionKeysFilterProp,
                                  setVersionKeysFilter: setVersionKeysFilterProp,
                                  formulasFilter: formulasFilterProp,
                                  setFormulasFilter: setFormulasFilterProp,
                                  rateTablesFilter: rateTablesFilterProp,
                                  setRateTablesFilter: setRateTablesFilterProp
                              }: VersionChangeSummaryProps) {
    const versionToCompare = compareVersion || previousVersion
    if (!versionToCompare) {
        return (
            <Box sx={{p: 3}}>
                <Typography variant="h6" sx={{mb: 2}}>Changes Summary</Typography>
                <Typography color="text.secondary">
                    This is the first version. I have nothing to compare it to. Godspeed!
                </Typography>
            </Box>
        )
    }

    // Compare version keys - use loaded data from versionKeys.json if available, fall back to version object
    const currentKeys = new Set(versionKeysContent ?? version.versionKeys ?? [])
    const compareKeys = new Set(compareVersionKeysContent ?? versionToCompare.versionKeys ?? [])
    const addedKeys = [...currentKeys].filter(k => !compareKeys.has(k))
    const removedKeys = [...compareKeys].filter(k => !currentKeys.has(k))

    const allVersionKeys = [...new Set([...currentKeys, ...compareKeys])].sort().map(key => ({
        key,
        status: addedKeys.includes(key) ? 'added' : removedKeys.includes(key) ? 'removed' : 'unchanged'
    })).filter(item => item.status !== 'unchanged')

    // Analyze formulas
    let allFormulas: Array<{
        path: string,
        domain: string,
        coverage: string,
        formula: string,
        status: 'added' | 'removed' | 'modified' | 'unchanged'
    }> = []

    if (versionFileContent?.formula && previousVersionFileContent?.formula) {
        const currentFormulas = versionFileContent.formula
        const previousFormulas = previousVersionFileContent.formula

        const getAllFormulaPaths = (formulas: any): Set<string> => {
            const paths = new Set<string>()
            Object.keys(formulas || {}).forEach(domain => {
                Object.keys(formulas[domain] || {}).forEach(coverage => {
                    Object.keys(formulas[domain][coverage] || {}).forEach(formula => {
                        paths.add(`${domain}/${coverage}/${formula}`)
                    })
                })
            })
            return paths
        }

        const currentPaths = getAllFormulaPaths(currentFormulas)
        const previousPaths = getAllFormulaPaths(previousFormulas)
        const added = [...currentPaths].filter(p => !previousPaths.has(p))
        const removed = [...previousPaths].filter(p => !currentPaths.has(p))
        const modified: string[] = []

        currentPaths.forEach(path => {
            if (previousPaths.has(path)) {
                const [domain, coverage, formula] = path.split('/')
                const current = currentFormulas[domain]?.[coverage]?.[formula]
                const previous = previousFormulas[domain]?.[coverage]?.[formula]

                const jsonCurrent = stringifySorted(current);
                const jsonPrevious = stringifySorted(previous);

                if (jsonCurrent !== jsonPrevious) {
                    modified.push(path)
                }
            }
        })

        const allPaths = [...new Set([...currentPaths, ...previousPaths])]
        allFormulas = allPaths.map(path => {
            const [domain, coverage, formula] = path.split('/')
            let status: 'added' | 'removed' | 'modified' | 'unchanged'
            if (added.includes(path)) status = 'added'
            else if (removed.includes(path)) status = 'removed'
            else if (modified.includes(path)) status = 'modified'
            else status = 'unchanged'

            return {path, domain, coverage, formula, status}
        }).filter(item => item.status !== 'unchanged').sort((a, b) => a.path.localeCompare(b.path))
    }

    // Analyze rate tables
    let allRateTables: Array<{ name: string, status: 'added' | 'removed' | 'modified' | 'unchanged' }> = []

    if (rateTableContent && previousRateTableContent &&
        typeof rateTableContent === 'object' && typeof previousRateTableContent === 'object') {
        const currentTables = Object.keys(rateTableContent).filter(key =>
            rateTableContent[key]?.headers && rateTableContent[key]?.rows
        )
        const previousTables = Object.keys(previousRateTableContent).filter(key =>
            previousRateTableContent[key]?.headers && previousRateTableContent[key]?.rows
        )

        const currentSet = new Set(currentTables)
        const previousSet = new Set(previousTables)
        const added = currentTables.filter(t => !previousSet.has(t))
        const removed = previousTables.filter(t => !currentSet.has(t))
        const modified: string[] = []

        currentTables.forEach(table => {
            if (previousSet.has(table)) {
                if (stringifySorted(rateTableContent[table]) !== stringifySorted(previousRateTableContent[table])) {
                    modified.push(table)
                }
            }
        })

        const allTableNames = [...new Set([...currentTables, ...previousTables])]
        allRateTables = allTableNames.map(name => {
            let status: 'added' | 'removed' | 'modified' | 'unchanged'
            if (added.includes(name)) status = 'added'
            else if (removed.includes(name)) status = 'removed'
            else if (modified.includes(name)) status = 'modified'
            else status = 'unchanged'

            return {name, status}
        }).filter(item => item.status !== 'unchanged').sort((a, b) => a.name.localeCompare(b.name))
    }

    // Group formulas by domain and coverage
    const groupedFormulas: Record<string, Record<string, typeof allFormulas>> = {}
    allFormulas.forEach(item => {
        if (!groupedFormulas[item.domain]) groupedFormulas[item.domain] = {}
        if (!groupedFormulas[item.domain][item.coverage]) groupedFormulas[item.domain][item.coverage] = []
        groupedFormulas[item.domain][item.coverage].push(item)
    })

    // Use props if provided, otherwise use local state as fallback
    const [localCollapsedDomains, setLocalCollapsedDomains] = useState<Set<string>>(new Set())
    const [localCollapsedCoverages, setLocalCollapsedCoverages] = useState<Set<string>>(new Set())
    const [localVersionKeysFilter, setLocalVersionKeysFilter] = useState<Set<string>>(new Set())
    const [localFormulasFilter, setLocalFormulasFilter] = useState<Set<string>>(new Set())
    const [localRateTablesFilter, setLocalRateTablesFilter] = useState<Set<string>>(new Set())

    const collapsedDomains = collapsedDomainsProp ?? localCollapsedDomains
    const setCollapsedDomains = setCollapsedDomainsProp ?? setLocalCollapsedDomains
    const collapsedCoverages = collapsedCoveragesProp ?? localCollapsedCoverages
    const setCollapsedCoverages = setCollapsedCoveragesProp ?? setLocalCollapsedCoverages
    const versionKeysFilter = versionKeysFilterProp ?? localVersionKeysFilter
    const setVersionKeysFilter = setVersionKeysFilterProp ?? setLocalVersionKeysFilter
    const formulasFilter = formulasFilterProp ?? localFormulasFilter
    const setFormulasFilter = setFormulasFilterProp ?? setLocalFormulasFilter
    const rateTablesFilter = rateTablesFilterProp ?? localRateTablesFilter
    const setRateTablesFilter = setRateTablesFilterProp ?? setLocalRateTablesFilter

    const toggleDomain = (domain: string) => {
        const next = new Set(collapsedDomains)
        if (next.has(domain)) {
            next.delete(domain)
        } else {
            next.add(domain)
        }
        setCollapsedDomains(next)
    }

    const toggleCoverage = (key: string) => {
        const next = new Set(collapsedCoverages)
        if (next.has(key)) {
            next.delete(key)
        } else {
            next.add(key)
        }
        setCollapsedCoverages(next)
    }

    const toggleCollapseAll = () => {
        const allDomains = Object.keys(filteredGroupedFormulas)
        if (collapsedDomains.size === allDomains.length) {
            // All collapsed, expand all
            setCollapsedDomains(new Set())
            setCollapsedCoverages(new Set())
        } else {
            // Some or none collapsed, collapse all
            setCollapsedDomains(new Set(allDomains))
        }
    }

    // Filter functions
    const toggleVersionKeysFilter = (status: string) => {
        const next = new Set(versionKeysFilter)
        if (next.has(status)) {
            next.delete(status)
        } else {
            next.add(status)
        }
        setVersionKeysFilter(next)
    }

    const toggleFormulasFilter = (status: string) => {
        const next = new Set(formulasFilter)
        if (next.has(status)) {
            next.delete(status)
        } else {
            next.add(status)
        }
        setFormulasFilter(next)
    }

    const toggleRateTablesFilter = (status: string) => {
        const next = new Set(rateTablesFilter)
        if (next.has(status)) {
            next.delete(status)
        } else {
            next.add(status)
        }
        setRateTablesFilter(next)
    }

    // Apply filters
    const filteredVersionKeys = versionKeysFilter.size > 0 ? allVersionKeys.filter(k => versionKeysFilter.has(k.status)) : allVersionKeys
    
    // Filter formulas by status and pinned version key
    let filteredFormulas = formulasFilter.size > 0 ? allFormulas.filter(f => formulasFilter.has(f.status)) : allFormulas
    if (pinnedVersionKey) {
        filteredFormulas = filteredFormulas.filter(f => {
            const formulaData = f.status !== 'removed'
                ? versionFileContent?.formula?.[f.domain]?.[f.coverage]?.[f.formula]
                : previousVersionFileContent?.formula?.[f.domain]?.[f.coverage]?.[f.formula]
            return formulaData?.ResolvedVersionKey?.versionKeyNumber === pinnedVersionKey
        })
    }
    
    // Filter rate tables by status and pinned version key
    let filteredRateTables = rateTablesFilter.size > 0 ? allRateTables.filter(t => rateTablesFilter.has(t.status)) : allRateTables
    if (pinnedVersionKey) {
        filteredRateTables = filteredRateTables.filter(t => {
            const resolvedVersionKey = t.status !== 'removed'
                ? rateTableContent?.[t.name]?.resolvedVersionKey
                : previousRateTableContent?.[t.name]?.resolvedVersionKey
            return resolvedVersionKey?.versionKeyNumber === pinnedVersionKey
        })
    }

    // Regroup filtered formulas
    const filteredGroupedFormulas: Record<string, Record<string, typeof allFormulas>> = {}
    filteredFormulas.forEach(item => {
        if (!filteredGroupedFormulas[item.domain]) filteredGroupedFormulas[item.domain] = {}
        if (!filteredGroupedFormulas[item.domain][item.coverage]) filteredGroupedFormulas[item.domain][item.coverage] = []
        filteredGroupedFormulas[item.domain][item.coverage].push(item)
    })

    // Helper to render stat link
    const renderStatLink = (label: string, count: number, isActive: boolean, onClick: () => void) => {
        if (count === 0) {
            return <span style={{
                padding: '1px 4px',
                borderRadius: '2px',
                transition: 'background-color 0.2s ease',
                display: 'inline-block'
            }}>{label}:<span style={{paddingLeft: '2px'}}>{count}</span></span>
        }
        return (
            <a
                onClick={onClick}
                style={{
                    cursor: 'pointer',
                    textDecoration: isActive ? 'underline' : 'none',
                    color: isActive ? '#333' : 'inherit',
                    backgroundColor: isActive ? '#ffeb3b' : 'transparent',
                    padding: '1px 4px',
                    borderRadius: '2px',
                    transition: 'background-color 0.2s ease',
                    display: 'inline-block'
                }}
                onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.currentTarget.style.textDecoration = isActive ? 'underline' : 'none'}
            >
                {label}:<span style={{paddingLeft: '2px'}}>{count}</span>
            </a>
        )
    }

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', marginTop: 5}}>
            <Box sx={{display: 'flex', gap: 3, flex: 1, minHeight: 0, overflow: 'hidden'}}>
                {/* Version Keys */}
                <Box sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    minHeight: 0,
                    overflow: 'hidden'
                }}>
                    <div className="section-wrapper">
                        <div className="section-title">
                            Version Keys
                        </div>
                        <div className="stats-wrapper">
                            <div>{renderStatLink('mod', 0, versionKeysFilter.has('modified'), () => toggleVersionKeysFilter('modified'))} {renderStatLink('new', allVersionKeys.filter(k => k.status === 'added').length, versionKeysFilter.has('added'), () => toggleVersionKeysFilter('added'))} {renderStatLink('del', allVersionKeys.filter(k => k.status === 'removed').length, versionKeysFilter.has('removed'), () => toggleVersionKeysFilter('removed'))}</div>
                        </div>
                    </div>
                    <Box sx={{
                        flexGrow: 1,
                        flexShrink: 1,
                        flexBasis: 0,
                        minHeight: 0,
                        maxHeight: '100%',
                        backgroundColor: versionKeysLoadError ? '#fff5f5' : '#fafafa',
                        border: `1px solid ${versionKeysLoadError ? '#ffcdd2' : '#ededed'}`,
                        padding: 2,
                        borderRadius: 2,
                        overflowY: 'auto',
                        overflowX: 'hidden'
                    }}>
                        {versionKeysLoadError ? (
                            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1}}>
                                <Typography variant="body2" sx={{color: '#c62828', fontWeight: 500}}>
                                    Failed to load version keys
                                </Typography>
                                <Typography variant="caption" sx={{color: '#666'}}>
                                    {versionKeysLoadError}
                                </Typography>
                            </Box>
                        ) : filteredVersionKeys.length > 0 ? (
                            <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
                                {filteredVersionKeys.map(({key, status}) => (
                                    <Box key={key}
                                         sx={{display: 'flex', alignItems: 'center', gap: 1, px: 0.5, py: 0.25}}>
                                        <span className={`status-badge status-badge-${status}`}/>
                                        <Typography variant="caption" sx={{fontFamily: 'monospace'}}>
                                        <span
                                            onMouseEnter={() => onVersionKeyHover?.(key)}
                                            onMouseLeave={() => onVersionKeyHover?.(null)}
                                            onClick={() => onVersionKeyTogglePin?.(key)}
                                            style={{
                                                cursor: 'pointer',
                                                padding: '2px 4px',
                                                borderRadius: '2px',
                                                backgroundColor: (hoveredVersionKey === key || pinnedVersionKey === key) ? '#ffeb3b' : 'transparent',
                                                transition: 'background-color 0.2s ease',
                                                display: 'inline-block'
                                            }}
                                        >
                                            {key}
                                        </span>
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary">No changes</Typography>
                        )}
                    </Box>
                </Box>

                {/* Formulas */}
                <Box sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    minHeight: 0,
                    overflow: 'hidden'
                }}>
                    <div className="section-wrapper">
                        <div className="section-title">
                            Rating Functions
                        </div>
                        <div className="stats-wrapper">
                            <div>
                                {renderStatLink('mod', allFormulas.filter(f => f.status === 'modified').length, formulasFilter.has('modified'), () => toggleFormulasFilter('modified'))} {renderStatLink('new', allFormulas.filter(f => f.status === 'added').length, formulasFilter.has('added'), () => toggleFormulasFilter('added'))} {renderStatLink('del', allFormulas.filter(f => f.status === 'removed').length, formulasFilter.has('removed'), () => toggleFormulasFilter('removed'))}
                            </div>
                            <div
                                onClick={toggleCollapseAll}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.color = '#333';
                                    e.currentTarget.style.textDecoration = 'underline'
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.color = '#666';
                                    e.currentTarget.style.textDecoration = 'none'
                                }}
                                style={{
                                    cursor: 'pointer',
                                    color: '#666',
                                    textDecoration: 'none'
                                }}
                            >
                                {collapsedDomains.size === Object.keys(filteredGroupedFormulas).length ? 'expand all' : 'collapse all'}
                            </div>
                        </div>
                    </div>
                    <Box sx={{
                        flexGrow: 1,
                        flexShrink: 1,
                        flexBasis: 0,
                        minHeight: 0,
                        maxHeight: '100%',
                        backgroundColor: formulasLoadError ? '#fff5f5' : '#fafafa',
                        border: `1px solid ${formulasLoadError ? '#ffcdd2' : '#ededed'}`,
                        padding: 2,
                        borderRadius: 2,
                        overflowY: 'auto',
                        overflowX: 'hidden'
                    }}>
                        {formulasLoadError ? (
                            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1}}>
                                <Typography variant="body2" sx={{color: '#c62828', fontWeight: 500}}>
                                    Failed to load formulas
                                </Typography>
                                <Typography variant="caption" sx={{color: '#666'}}>
                                    {formulasLoadError}
                                </Typography>
                            </Box>
                        ) : filteredFormulas.length > 0 ? (
                            <Box sx={{display: 'flex', flexDirection: 'column'}}>
                                {Object.entries(filteredGroupedFormulas).map(([domain, coverages]) => {
                                    const isDomainCollapsed = collapsedDomains.has(domain)
                                    return (
                                        <Box key={domain} sx={{mb: 2}}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: '#555',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    mb: isDomainCollapsed ? 0 : 2,
                                                    cursor: 'pointer',
                                                    userSelect: 'none',
                                                    fontSize: '12px',
                                                    '&:hover': {color: '#333'}
                                                }}
                                                onClick={() => toggleDomain(domain)}
                                            >
                                                <ChevronRight sx={{
                                                    fontSize: '16px',
                                                    transform: isDomainCollapsed ? 'rotate(0deg)' : 'rotate(90deg)',
                                                    transition: 'transform 0.2s ease'
                                                }}/>
                                                {domain}
                                            </Typography>
                                            <Box sx={{
                                                display: 'grid',
                                                gridTemplateRows: isDomainCollapsed ? '0fr' : '1fr',
                                                transition: 'grid-template-rows 0.3s ease',
                                                '& > div': {
                                                    overflow: 'hidden'
                                                }
                                            }}>
                                                <div>
                                                    {Object.entries(coverages).map(([coverage, formulas]) => {
                                                        const coverageKey = `${domain}/${coverage}`
                                                        const isCoverageCollapsed = collapsedCoverages.has(coverageKey)
                                                        return (
                                                            <Box key={coverage} sx={{ml: 2, mb: 2}}>
                                                                {coverage !== 'NoCoverage' && (
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            mb: isCoverageCollapsed ? 0 : 0.5,
                                                                            cursor: 'pointer',
                                                                            userSelect: 'none',
                                                                            fontSize: '12px',
                                                                            '&:hover': {color: '#333'}
                                                                        }}
                                                                        onClick={() => toggleCoverage(coverageKey)}
                                                                    >
                                                                        <ChevronRight sx={{
                                                                            fontSize: '16px',
                                                                            transform: isCoverageCollapsed ? 'rotate(0deg)' : 'rotate(90deg)',
                                                                            transition: 'transform 0.2s ease'
                                                                        }}/>
                                                                        {coverage}
                                                                    </Typography>
                                                                )}
                                                                <Box sx={{
                                                                    display: 'grid',
                                                                    gridTemplateRows: isCoverageCollapsed ? '0fr' : '1fr',
                                                                    transition: 'grid-template-rows 0.3s ease',
                                                                    '& > div': {
                                                                        overflow: 'hidden'
                                                                    }
                                                                }}>
                                                                    <div>
                                                                        <Box sx={{
                                                                            ml: coverage !== 'NoCoverage' ? 0 : 0,
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            gap: 0.25
                                                                        }}>
                                                                            {formulas.map(({formula, status, path}) => {
                                                                                const formulaData = status !== 'removed'
                                                                                    ? versionFileContent?.formula?.[domain]?.[coverage]?.[formula]
                                                                                    : previousVersionFileContent?.formula?.[domain]?.[coverage]?.[formula]
                                                                                const resolvedVersionKey = formulaData?.ResolvedVersionKey
                                                                                const versionKey = resolvedVersionKey?.versionKeyNumber

                                                                                return (
                                                                                    <Box key={formula} sx={{
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        gap: 1,
                                                                                        px: 0.5,
                                                                                        py: 0.25,
                                                                                        borderRadius: 0.5,
                                                                                        '&:hover': {backgroundColor: '#f5f5f5'}
                                                                                    }}>
                                                                                        <span
                                                                                            className={`status-badge status-badge-${status}`}/>
                                                                                        <Box sx={{
                                                                                            display: 'flex',
                                                                                            alignItems: 'center',
                                                                                            justifyContent: 'space-between',
                                                                                            flex: 1
                                                                                        }}>
                                                                                            <Typography
                                                                                                // variant="caption"
                                                                                                className={onNavigateToFormula ? 'clickable-link' : ''}
                                                                                                sx={{
                                                                                                    fontFamily: 'monospace',
                                                                                                    fontSize: '11px'
                                                                                                }}
                                                                                                onClick={() => onNavigateToFormula?.(path)}
                                                                                            >
                                                                                                {formula}
                                                                                            </Typography>
                                                                                            {versionKey ? (
                                                                                                <Tooltip
                                                                                                    title={
                                                                                                        <Box
                                                                                                            sx={{p: 0.5}}>
                                                                                                            <Typography
                                                                                                                variant="caption"
                                                                                                                sx={{
                                                                                                                    display: 'block',
                                                                                                                    fontWeight: 600
                                                                                                                }}>
                                                                                                                Resolved
                                                                                                                Version
                                                                                                                Key
                                                                                                            </Typography>
                                                                                                            <Typography
                                                                                                                variant="caption"
                                                                                                                sx={{
                                                                                                                    display: 'block',
                                                                                                                    mt: 0.5
                                                                                                                }}>
                                                                                                                ID: {resolvedVersionKey.versionKeyID}
                                                                                                            </Typography>
                                                                                                            <Typography
                                                                                                                variant="caption"
                                                                                                                sx={{display: 'block'}}>
                                                                                                                Number: {resolvedVersionKey.versionKeyNumber}
                                                                                                            </Typography>
                                                                                                            <Typography
                                                                                                                variant="caption"
                                                                                                                sx={{display: 'block'}}>
                                                                                                                Effective
                                                                                                                Date: {resolvedVersionKey.effectiveDate}
                                                                                                            </Typography>
                                                                                                            <Typography
                                                                                                                variant="caption"
                                                                                                                sx={{display: 'block'}}>
                                                                                                                Status: {resolvedVersionKey.status}
                                                                                                            </Typography>
                                                                                                            <Typography
                                                                                                                variant="caption"
                                                                                                                sx={{display: 'block'}}>
                                                                                                                Bureau: {resolvedVersionKey.bureau ? 'Yes' : 'No'}
                                                                                                            </Typography>
                                                                                                        </Box>
                                                                                                    }
                                                                                                    placement="top"
                                                                                                    arrow
                                                                                                    enterDelay={500}
                                                                                                >
                                                                        <span
                                                                            className="version-key-display"
                                                                            style={{
                                                                                backgroundColor: (hoveredVersionKey === versionKey || pinnedVersionKey === versionKey) ? '#ffeb3b' : 'transparent',
                                                                                padding: '2px 4px',
                                                                                borderRadius: '2px',
                                                                                transition: 'background-color 0.2s ease'
                                                                            }}
                                                                            onClick={() => onVersionKeyTogglePin?.(versionKey)}
                                                                        >
                                                                            {versionKey}
                                                                        </span>
                                                                                                </Tooltip>
                                                                                            ) : (
                                                                                                <span
                                                                                                    className="client-override"/>
                                                                                            )}
                                                                                        </Box>
                                                                                    </Box>
                                                                                )
                                                                            })}
                                                                        </Box>
                                                                    </div>
                                                                </Box>
                                                            </Box>
                                                        )
                                                    })}
                                                </div>
                                            </Box>
                                        </Box>
                                    )
                                })}
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary">No changes</Typography>
                        )}
                    </Box>
                </Box>

                {/* Rate Tables */}
                <Box sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    minHeight: 0,
                    overflow: 'hidden'
                }}>
                    <div className="section-wrapper">
                        <div className="section-title">
                            Rate Tables
                        </div>
                        <div className="stats-wrapper">
                            <div>{renderStatLink('mod', allRateTables.filter(t => t.status === 'modified').length, rateTablesFilter.has('modified'), () => toggleRateTablesFilter('modified'))} {renderStatLink('new', allRateTables.filter(t => t.status === 'added').length, rateTablesFilter.has('added'), () => toggleRateTablesFilter('added'))} {renderStatLink('del', allRateTables.filter(t => t.status === 'removed').length, rateTablesFilter.has('removed'), () => toggleRateTablesFilter('removed'))}</div>
                        </div>
                    </div>
                    <Box sx={{
                        flexGrow: 1,
                        flexShrink: 1,
                        flexBasis: 0,
                        minHeight: 0,
                        maxHeight: '100%',
                        backgroundColor: rateTablesLoadError ? '#fff5f5' : '#fafafa',
                        border: `1px solid ${rateTablesLoadError ? '#ffcdd2' : '#ededed'}`,
                        padding: 2,
                        borderRadius: 2,
                        overflowY: 'auto',
                        overflowX: 'hidden'
                    }}>
                        {rateTablesLoadError ? (
                            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1}}>
                                <Typography variant="body2" sx={{color: '#c62828', fontWeight: 500}}>
                                    Failed to load rate tables
                                </Typography>
                                <Typography variant="caption" sx={{color: '#666'}}>
                                    {rateTablesLoadError}
                                </Typography>
                            </Box>
                        ) : filteredRateTables.length > 0 ? (
                            <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
                                {filteredRateTables.map(({name, status}) => {
                                    const resolvedVersionKey = status !== 'removed'
                                        ? rateTableContent?.[name]?.resolvedVersionKey
                                        : previousRateTableContent?.[name]?.resolvedVersionKey
                                    const versionKey = resolvedVersionKey?.versionKeyNumber

                                    return (
                                        <Box key={name} sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            px: 0.5,
                                            py: 0.25,
                                            borderRadius: 0.5,
                                            '&:hover': {backgroundColor: '#f5f5f5'}
                                        }}>
                                            <span className={`status-badge status-badge-${status}`}/>
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                flex: 1
                                            }}>
                                                <Typography
                                                    variant="caption"
                                                    className={onNavigateToRateTable ? 'clickable-link' : ''}
                                                    sx={{fontFamily: 'monospace'}}
                                                    onClick={() => onNavigateToRateTable?.(name)}
                                                >
                                                    {name}
                                                </Typography>
                                                {versionKey ? (
                                                    <Tooltip
                                                        title={
                                                            <Box sx={{p: 0.5}}>
                                                                <Typography variant="caption"
                                                                            sx={{display: 'block', fontWeight: 600}}>
                                                                    Resolved Version Key
                                                                </Typography>
                                                                <Typography variant="caption"
                                                                            sx={{display: 'block', mt: 0.5}}>
                                                                    ID: {resolvedVersionKey.versionKeyID}
                                                                </Typography>
                                                                <Typography variant="caption" sx={{display: 'block'}}>
                                                                    Number: {resolvedVersionKey.versionKeyNumber}
                                                                </Typography>
                                                                <Typography variant="caption" sx={{display: 'block'}}>
                                                                    Effective Date: {resolvedVersionKey.effectiveDate}
                                                                </Typography>
                                                                <Typography variant="caption" sx={{display: 'block'}}>
                                                                    Status: {resolvedVersionKey.status}
                                                                </Typography>
                                                                <Typography variant="caption" sx={{display: 'block'}}>
                                                                    Bureau: {resolvedVersionKey.bureau ? 'Yes' : 'No'}
                                                                </Typography>
                                                            </Box>
                                                        }
                                                        placement="top"
                                                        arrow
                                                    >
                                                    <span
                                                        className="version-key-display"
                                                        style={{
                                                            backgroundColor: (hoveredVersionKey === versionKey || pinnedVersionKey === versionKey) ? '#ffeb3b' : 'transparent',
                                                            padding: '2px 4px',
                                                            borderRadius: '2px',
                                                            transition: 'background-color 0.2s ease'
                                                        }}
                                                    >
                                                        {versionKey}
                                                    </span>
                                                    </Tooltip>
                                                ) : (
                                                    <span className="client-override"/>
                                                )}
                                            </Box>
                                        </Box>
                                    )
                                })}
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary">No changes</Typography>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

export default VersionChangeSummary
